import React, { useContext, useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import { userSchema } from "../validations/userSchema";
import { AuthContext } from '../context/authContext';
import { Input, Label, Button } from "../components/ui";
import { useSearchParams } from 'react-router-dom';
import { optionsPartido } from '../datos/options';


// ... Importaciones de componentes UI (Input, Label, Button, FormField) y AuthContext ...

export const RegisterPage = () => {
    const { login, setUser } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState(null);
    const [tokenValidating, setTokenValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenData, setTokenData] = useState(null);
    const firstInputRef = useRef(null);
    
    // Estados para mostrar/ocultar contraseñas
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // Iconos SVG para el toggle de contraseña
    const EyeIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    );
    
    const EyeSlashIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L6.228 6.228" />
        </svg>
    );
    
    const iconColorClass = 'text-gray-400 hover:text-gray-500';
    
    const [searchParams] = useSearchParams();
    const registrationToken = searchParams.get('token');

    // Validate token on mount
    useEffect(() => {
        const validateToken = async () => {
            if (!registrationToken) {
                setTokenValidating(false);
                setTokenValid(false);
                setMessage("Acceso denegado. Se requiere un token de registro válido.");
                setMessageType("error");
                return;
            }

            try {
                const response = await fetch(`/api/user-requests/validate-token/${registrationToken}`);
                const data = await response.json();

                if (data.valid) {
                    setTokenValid(true);
                    setTokenData(data.request);
                    setMessage(`Token válido. Complete su registro para ${data.request.nombre_municipio}.`);
                    setMessageType("success");
                } else {
                    setTokenValid(false);
                    setMessage("Token inválido o expirado. Por favor solicite un nuevo acceso.");
                    setMessageType("error");
                }
            } catch (error) {
                setTokenValid(false);
                setMessage("Error al validar el token. Por favor intente nuevamente.");
                setMessageType("error");
            } finally {
                setTokenValidating(false);
            }
        };

        validateToken();
    }, [registrationToken]);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        setValue
    } = useForm({
        resolver: zodResolver(userSchema),
        shouldUnregister: false
    });

    // Debug: log validation errors
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            console.log('Form validation errors:', errors);
        }
    }, [errors]);

    // Set email and municipality from token data
    useEffect(() => {
        if (tokenData) {
            setValue('email', tokenData.email_responsable);
            setValue('user_municipio', tokenData.codigo_partido);
        }
    }, [tokenData, setValue]);

    const onSubmit = async (data) => {
        if (!tokenValid || !registrationToken) {
            setMessage("No se puede registrar sin un token válido.");
            setMessageType("error");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`/api/authentication/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...data,
                    registrationToken,
                    user_municipio: tokenData.codigo_partido
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Registration failed');
            }

            const { jwtToken, name, role, userId, user_municipio } = await response.json();

            if (jwtToken) {
                login(name, jwtToken, role, userId, false, false, user_municipio);
            }
        } catch (error) {
            console.error("Registration error:", error.message);
            setMessage(error.message);
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };

    // Show loading while validating token
    if (tokenValidating) {
        return (
            <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Validando token de registro...</p>
                </div>
            </div>
        );
    }

    // Show error if token is invalid
    if (!tokenValid) {
        return (
            <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
                <div className="sm:mx-auto sm:w-full sm:max-w-lg">
                    <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <h3 className="mt-6 text-lg font-medium text-gray-900 dark:text-white">Acceso Denegado</h3>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                {message || "Se requiere un token de registro válido para acceder a esta página."}
                            </p>
                            <div className="mt-6">
                                <a href="/solicitud-usuario" className="text-blue-600 hover:text-blue-500 font-medium">
                                    Solicitar acceso al sistema →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
            <div className="sm:mx-auto sm:w-full sm:max-w-lg">
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Registro de usuario
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    RED - Registro de Entidades Deportivas - {tokenData?.nombre_municipio}
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    
                    {message && messageType === "success" && (
                        <div className="mb-4 rounded-md bg-green-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-green-800">{message}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {message && messageType === "error" && (
                        <div className="mb-4 rounded-md bg-red-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-red-800">{message}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate>
                        {/* Municipio - Read only from token */}
                        <div>
                            <Label htmlFor="user_municipio">Municipio</Label>
                            <select
                                id="user_municipio"
                                {...register('user_municipio')}
                                readOnly
                                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                                {optionsPartido.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">El municipio está predefinido según su solicitud aprobada.</p>
                        </div>

                        {/* Email - Read only from token */}
                        <div>
                            <Label htmlFor="email">Correo Electrónico</Label>
                            <Input
                                id="email"
                                type="email"
                                {...register('email')}
                                readOnly
                                className="bg-gray-100"
                            />
                            <p className="mt-1 text-xs text-gray-500">El email está predefinido según su solicitud.</p>
                            {errors.email?.message && (
                                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                            )}
                        </div>

                        {/* Username */}
                        <div>
                            <Label htmlFor="username">Nombre de Usuario</Label>
                            <Input
                                id="username"
                                type="text"
                                placeholder="Su nombre de usuario"
                                {...register('username')}
                            />
                            {errors.username?.message && (
                                <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <Label htmlFor="password">Contraseña</Label>
                            <div className="mt-1" style={{ position: 'relative' }}>
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="********"
                                    {...register('password')}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className={`absolute inset-y-0 right-0 flex items-center pr-3 text-xl leading-none cursor-pointer focus:outline-none ${iconColorClass}`}
                                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                >
                                    {showPassword ? EyeSlashIcon : EyeIcon}
                                </button>
                            </div>
                            {errors.password?.message && (
                                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                            <div className="mt-1" style={{ position: 'relative' }}>
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="********"
                                    {...register('confirmPassword')}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className={`absolute inset-y-0 right-0 flex items-center pr-3 text-xl leading-none cursor-pointer focus:outline-none ${iconColorClass}`}
                                    aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                >
                                    {showConfirmPassword ? EyeSlashIcon : EyeIcon}
                                </button>
                            </div>
                            {errors.confirmPassword?.message && (
                                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>
                            )}
                        </div>

                        <div>
                            <Button
                                variant="subse"
                                type="submit" 
                                disabled={loading || !watch('username') || !watch('password') || !watch('confirmPassword')}
                            >
                                {loading ? (
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : 'Registrarse'}
                            </Button>
                        </div>
                    </form>

                    <div className="mt-6">
                        <p className="text-xs text-gray-500 text-center">
                            Este enlace de registro es válido por 7 días y solo puede usarse una vez.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
